import type {
  KeyObjectType,
  KeyphraseCategory,
  KeywordCategory,
  KeywordsAndPhrasesReducerState,
  PopoverTab,
  SingleKey,
} from './KeywordsAndPhrasesReducer.d'
import {
  escapeRegExp,
  getKeywordMatchingRegexTest,
} from 'components/KeywordsElements/Highlighting/HighlightedTextBodySpan/utils'
import type { JobKeywordsData, v5 } from '@contracts'

export const getKeywordsList = (
  keywordsAndPhrases: JobKeywordsData | undefined,
): Record<keyof KeywordCategory, string[]> | null => {
  if (!keywordsAndPhrases) return null
  const { required, optional, omit, priority } = keywordsAndPhrases
  return {
    priority: priority || [],
    required: required || [],
    optional: optional || [],
    avoid: omit || [],
  }
}

export const getKeyphrasesList = (
  keywordsAndPhrases: JobKeywordsData | undefined,
): Record<keyof KeyphraseCategory, string[]> | null => {
  if (!keywordsAndPhrases) return null
  const { keyphrases_required, keyphrases_optional } = keywordsAndPhrases
  return {
    keyphrases_required: keyphrases_required || [],
    keyphrases_optional: keyphrases_optional || [],
  }
}

export const filterAndSortKeywordsOrPhrases = (keywordsOrPhrases: string[]) => {
  // Remove empty keyphrases from the array
  const filteredKeyphrases = keywordsOrPhrases.filter(keyphrase => keyphrase.trim() !== '')

  // Remove duplicates
  const uniqueKeyphrases = Array.from(new Set(filteredKeyphrases))

  // Sort the keyphrases by length in descending order
  const sortedKeyphrases = uniqueKeyphrases.sort((a, b) => b.length - a.length)

  return sortedKeyphrases
}

export const getEscapedKeyphrases = (formattedKeyphrases: string[]) => {
  // Escape special characters in the keywords to avoid regex issues
  const escapedKeyphrases = formattedKeyphrases.map(keyword => escapeRegExp(keyword))

  // Create a regular expression pattern to match the keywords
  return new RegExp(`(${escapedKeyphrases.join('|')})`, 'gi')
}

export function getCount(
  inputString: string,
  keyphrases: string[],
  returnObject: { [key: string]: { count: number; originalCount: number } },
) {
  if (!keyphrases.length) return inputString

  const formattedKeyphrases = filterAndSortKeywordsOrPhrases(keyphrases)
  const keywordPattern = getEscapedKeyphrases(formattedKeyphrases)

  // Split the input string into an array of substrings and matches
  const parts = inputString.split(keywordPattern)

  // Create an array of JSX elements
  parts.forEach(part => {
    if (keywordPattern.test(part)) {
      const nestedKeyphrases = formattedKeyphrases.filter(keyphrase => keyphrase !== part && part.includes(keyphrase))
      if (!Reflect.has(returnObject, part)) returnObject[part] = { count: 0, originalCount: 0 }
      returnObject[part].count += 1
      returnObject[part].originalCount += 1

      // passing return object by reference should include nested matches in the final count
      if (nestedKeyphrases.length > 0) getCount(part, nestedKeyphrases, returnObject)
    }
  })
}

export const getCountStrings = (keyword: string, inputStrings: (string | string[] | undefined)[] | undefined) => {
  const trimmedKeyword = keyword.trim()
  const escapedKeyword = escapeRegExp(trimmedKeyword)
  const regex = new RegExp(getKeywordMatchingRegexTest(escapedKeyword), 'gi')

  const inputStringArrays = inputStrings
    ?.map(inputString => {
      return typeof inputString === 'string' ? [inputString] : inputString
    })
    .flat()

  const count = inputStringArrays?.reduce((total, inputString) => {
    const matches = (inputString?.match(regex) || []).length
    return total + matches
  }, 0)

  return count
}

// get the keywords or phrases from the response
function getKeywordsOrPhrases(type: string, keyType: KeyObjectType, config: v5.JobsDetailData['config']) {
  switch (type) {
    case 'product':
      return keyType === 'keyword'
        ? getKeywordsList(config?.product?.keywords)
        : getKeyphrasesList(config?.product?.keywords)
    case 'brand':
      return keyType === 'keyword'
        ? getKeywordsList(config?.brand?.brand_keywords)
        : getKeyphrasesList(config?.brand?.brand_keywords)
  }
}

// get the count of each keyword in the content
function getKeywordCounts(
  keywordStrings: string[],
  currentContent: (string | string[] | undefined)[] | undefined,
  originalContent: (string | string[] | undefined)[] | undefined,
) {
  if (!keywordStrings) return []
  // Get the content and original content from the content items
  return keywordStrings.map(keywordOrPhrase => {
    const currentCount = getCountStrings(keywordOrPhrase, currentContent)
    const originalCount = getCountStrings(keywordOrPhrase, originalContent)
    return {
      key: keywordOrPhrase,
      currentCount,
      originalCount,
    } as SingleKey
  })
}

/**
 * Retrieves the keywords state based on the payload and current state.
 *
 * @param payload - The payload containing the product and content items (JobsDetailData)
 * @param state - The current state of the KeywordsAndPhrasesReducer.
 * @returns The updated keywords state.
 */
export function getKeywordsState<T extends KeywordCategory | KeyphraseCategory>(
  payload: v5.JobsDetailData,
  state: KeywordsAndPhrasesReducerState,
  keyType: KeyObjectType,
): PopoverTab<T> {
  const { content_items, config } = payload
  const keys = ['product', 'brand', 'retailer'] as (keyof PopoverTab<T>)[]

  const currentContent = content_items?.map(content_item => content_item.content)
  const originalContent = content_items?.map(content_item => content_item.original_content)

  // Create a deep copy of the keywords object to store the count of each keyword in the content (e.g. { required: [{ key: 'requiredKeyword1', currentCount: 1, originalCount: 1 }, ...], ...})
  const popoverTabKeywords: PopoverTab<T> = JSON.parse(
    JSON.stringify(state[keyType === 'keyword' ? 'keywords' : 'keyphrases']),
  )

  keys.forEach(type => {
    const keywordsOrPhrases = getKeywordsOrPhrases(type, keyType, config)

    // For each keyword category, e.g. required, optional, etc., get the count of each keyword in the content
    if (!keywordsOrPhrases) return []
    Object.entries(keywordsOrPhrases).forEach(([keywordCategoryKey, keywordStrings]) => {
      if (keywordStrings?.length === 1 && keywordStrings[0] === '') return []

      // For each keyword in the category, get the count of the keyword in the content
      const keywordCounts = getKeywordCounts(keywordStrings, currentContent, originalContent)
      // Set the count of each keyword in the popoverTabKeywords object
      // NOTE: casting to any is necessary because the type of the key is unknown at compile time
      popoverTabKeywords[type][keywordCategoryKey as keyof T] = keywordCounts as any
    })
  })
  return popoverTabKeywords
}
